import type { AvatarLookConfig } from './generated/avatar-runtime';
import type { MascotAnimationName } from './contextAnimations';

export const LEGACY_MASCOT_IDS = [
  'strobi',
  'freddy',
  'citrus',
  'nova',
  'grok-bot',
  'kirby',
  'cloudee',
  'cubee',
  'onee',
] as const;

export const NEW_MASCOT_IDS = [
  'totto',
  'cometa',
  'nibbli',
  'cresto',
  'duo',
  'penta',
  'jelli',
  'prisma',
] as const;

/**
 * Dedicated Orb exports. They are present in the catalogue but deliberately
 * kept out of the generic rotation: the normalized player name owns the shape.
 */
export const NAMED_MASCOT_IDS = [
  'marine',
  'bouguasse',
  'quentin',
  'yve',
  'karine',
  'stef',
  'estelle',
] as const;

export const GENERAL_MASCOT_IDS = [...LEGACY_MASCOT_IDS, ...NEW_MASCOT_IDS] as const;
export const MASCOT_IDS = [...GENERAL_MASCOT_IDS, ...NAMED_MASCOT_IDS] as const;

export type MascotId = (typeof MASCOT_IDS)[number];
export type NamedMascotId = (typeof NAMED_MASCOT_IDS)[number];

export function isNamedMascotId(value: MascotId): value is NamedMascotId {
  return (NAMED_MASCOT_IDS as readonly MascotId[]).includes(value);
}

export const NAMED_MASCOT_BY_PLAYER_KEY: Readonly<
  Record<string, NamedMascotId>
> = Object.freeze({
  marine: 'marine',
  bougasse: 'bouguasse',
  // Compatibility key consumed once while old saved tables are rewritten.
  bouguasse: 'bouguasse',
  quentin: 'quentin',
  yves: 'yve',
  karine: 'karine',
  stef: 'stef',
  estelle: 'estelle',
});

export function namedMascotForPlayerName(
  value: string,
): NamedMascotId | undefined {
  const key = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('fr');
  return NAMED_MASCOT_BY_PLAYER_KEY[key];
}

/**
 * Generic identities keep their persisted mascot. Dedicated names bypass this
 * order, while empty slots and newly created generic identities consume it.
 */
export const NEW_PLAYER_MASCOT_ORDER: readonly MascotId[] = [
  ...NEW_MASCOT_IDS,
  ...LEGACY_MASCOT_IDS,
];
export type MascotReaction = MascotAnimationName;
export type MascotLook = AvatarLookConfig;
export type MascotMotion =
  | 'calm'
  | 'bounce'
  | 'tremble'
  | 'sulk'
  | 'swagger'
  | 'peek'
  | 'relief'
  | 'stunned';

export interface PlayerAppearance {
  mascotId: MascotId;
  colorIndex: number;
}
